/**
 * Capability checks for the short-term video-recall (DVR) feature.
 *
 * Recall plays the MediaMTX HLS window as H.265/HEVC. That is a *separate* decode
 * path from the live WebRTC stream, so the fact that WebRTC-HEVC works does NOT
 * guarantee HLS-over-MSE HEVC works. We gate the rewind UI on these checks and hide
 * it entirely where the browser cannot decode HEVC HLS.
 *
 * Two viable paths:
 *  - Safari: native HLS (`application/vnd.apple.mpegurl`) with hardware HEVC.
 *  - Chrome/Edge on Windows: hls.js + MSE, where the browser's MSE must support
 *    `video/mp4; codecs="hvc1..."` (requires a hardware HEVC decoder).
 */

/** True when the browser can decode HEVC fMP4 through Media Source Extensions (hls.js path). */
export const canPlayHevcMse = (): boolean =>
  typeof MediaSource !== 'undefined' &&
  typeof MediaSource.isTypeSupported === 'function' &&
  // hvc1.1.6.L93.B0 ~= Main profile, level 3.1 — a conservative baseline probe.
  MediaSource.isTypeSupported('video/mp4; codecs="hvc1.1.6.L93.B0"');

/** True when the browser can play HEVC HLS natively (Safari). */
const canPlayHevcNativeHls = (): boolean => {
  if (typeof document === 'undefined') return false;
  const video = document.createElement('video');
  return (
    video.canPlayType('application/vnd.apple.mpegurl') !== '' &&
    video.canPlayType('video/mp4; codecs="hvc1"') !== ''
  );
};

/**
 * True when this browser can play the H.265 HLS recall stream by either path.
 * Use this to decide whether to expose the rewind controls at all.
 */
export const canPlayHlsRecall = (): boolean => canPlayHevcNativeHls() || canPlayHevcMse();
